import { VStack } from '@chakra-ui/react';
import {
  FETCH_BATCH_SIZE,
  TARGET_INITIAL_COUNT,
  TARGET_LOAD_MORE_COUNT,
  useTaxonomyAuditLogs,
} from 'Features/History/History.hooks';
import {
  HistorySideBarFiltering,
  HistorySidebar,
  HistorySidebarHeader,
} from 'Features/History/HistorySidebar';
import { Drawer } from 'Molecules';
import { CompanyAssessment } from 'models';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const AssessmentHistory = ({
  cAssessment,
  isHistorySidebarOpen,
  setIsHistorySidebarOpen,
}: {
  cAssessment: CompanyAssessment;
  isHistorySidebarOpen: boolean;
  setIsHistorySidebarOpen: (open: boolean) => void;
}) => {
  const { t: t1 } = useTranslation('files');
  const { t: t2 } = useTranslation('audit');
  const [searchValue, setSearchValue] = useState('');
  const shouldRenderContent = !!cAssessment && isHistorySidebarOpen;
  const [auditLogsPagination, setAuditLogsPagination] = useState({
    limit: 0,
    offset: 0,
  });
  const [targetCount, setTargetCount] = useState(TARGET_INITIAL_COUNT);

  const {
    auditLogs: auditLogsData,
    loading: auditLogsLoading,
    refetch: refetchAuditLogs,
    hasMoreData,
    refetchLoading,
  } = useTaxonomyAuditLogs({ cAssessment, ...auditLogsPagination, skip: !shouldRenderContent });

  const handleRefetchAuditLogs = async () => {
    setTargetCount(TARGET_INITIAL_COUNT);
    setAuditLogsPagination({ limit: TARGET_INITIAL_COUNT, offset: 0 });
    await refetchAuditLogs();
  };

  const incrementTargetCount = () => {
    setTargetCount((prev) => prev + TARGET_LOAD_MORE_COUNT);
  };
  const loadMore = () => {
    setAuditLogsPagination((prev) => ({
      limit: FETCH_BATCH_SIZE,
      offset: prev.offset + prev.limit,
    }));
  };

  useEffect(() => {
    if (isHistorySidebarOpen) {
      setTargetCount(TARGET_INITIAL_COUNT);
      setAuditLogsPagination({ limit: TARGET_INITIAL_COUNT, offset: 0 });
    }
  }, [isHistorySidebarOpen]);

  return (
    <Drawer
      onClose={() => {
        setIsHistorySidebarOpen(false);
      }}
      isOpen={shouldRenderContent}
      width="400px"
      hasOverlay={false}
      returnFocusOnClose={false}
      header={
        shouldRenderContent ? (
          <VStack width="100%" paddingBottom="0px">
            <HistorySidebarHeader t={t1} />
            <HistorySideBarFiltering
              title={cAssessment?.aggregate.title ?? ''}
              refetchAuditLogs={handleRefetchAuditLogs}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              t={t2}
            />
          </VStack>
        ) : undefined
      }
      headerProps={{
        paddingBottom: '0px',
        borderLeft: '1px solid',
        borderColor: 'border.decorative',
      }}
      bodyProps={{
        padding: '0px',
        borderLeft: '1px solid',
        borderColor: 'border.decorative',
      }}
    >
      {shouldRenderContent && (
        <HistorySidebar
          key="history-drawer"
          auditLogs={auditLogsData}
          loading={auditLogsLoading}
          loadMore={loadMore}
          searchValue={searchValue}
          hasMoreData={hasMoreData}
          refetchLoading={refetchLoading}
          targetCount={targetCount}
          incrementTargetCount={incrementTargetCount}
        />
      )}
    </Drawer>
  );
};
